import { faQrcode } from '@fortawesome/free-solid-svg-icons';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AtiraColumn } from '../../components/AtiraColumn';
import { AtiraEmpty } from '../../components/AtiraEmpty';
import { AtiraIcon } from '../../components/AtiraIcon';
import { AtiraRow } from '../../components/AtiraRow';
import { Flex } from '../../components/Flex';
import { Text } from '../../components/Text';
import { QRSliceSelectors } from '../../redux/QR/qr.selector';
import { useAppSelector } from '../../redux/store';
import { Breakpoints } from '../../theme/Breakpoints';
import { Rounded } from '../../theme/Rounded';
import { Spacing } from '../../theme/Spacing';
import { QRCode } from './components/QRCode';

const StyledColumn = styled(AtiraColumn)`
  padding-bottom: 20px !important;

  &:nth-child(odd) {
    padding-inline-end: 5px !important;
  }

  &:nth-child(even) {
    padding-inline-start: 5px !important;
  }

  @media (min-width: ${Breakpoints.TABLET}) {
    &:nth-child(even) {
      padding-inline-start: 0 !important;
    }
    &:nth-child(odd) {
      padding-inline-end: 0 !important;
    }

    &:nth-child(3n - 2) {
      padding-inline-end: 10px !important;
    }
    &:nth-child(3n - 1) {
      padding-inline: 5px !important;
    }
    &:nth-child(3n) {
      padding-inline-start: 10px !important;
    }
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    &:nth-child(3n - 2) {
      padding-inline-end: 0 !important;
    }
    &:nth-child(3n - 1) {
      padding-inline: 0 !important;
    }
    &:nth-child(3n) {
      padding-inline-start: 0 !important;
    }

    &:nth-child(4n - 3) {
      padding-inline-end: 10px !important;
    }
    &:nth-child(4n - 2),
    &:nth-child(4n - 1) {
      padding-inline: 10px !important;
    }
    &:nth-child(4n) {
      padding-inline-start: 10px !important;
    }
  }

  @media (min-width: ${Breakpoints.LARGE_DESKTOP}) {
    &:nth-child(4n - 3) {
      padding-inline-end: 0 !important;
    }
    &:nth-child(4n - 2),
    &:nth-child(4n - 1) {
      padding-inline: 0 !important;
    }
    &:nth-child(4n) {
      padding-inline-start: 0 !important;
    }

    &:nth-child(6n - 5) {
      padding-inline-end: 10px !important;
    }
    &:nth-child(6n - 4),
    &:nth-child(6n - 3),
    &:nth-child(6n - 2),
    &:nth-child(6n - 1) {
      padding-inline: 10px !important;
    }
    &:nth-child(6n) {
      padding-inline-start: 10px !important;
    }
  }
`;

const PlusIconWrapper = styled(Flex)`
  display: inline-flex;
  width: 1.5rem;
  justify-content: center;
  align-items: center;
  aspect-ratio: 1 / 1;
  background-color: ${({ theme }) => theme.main};
  border-radius: ${Rounded.circle};
  padding-inline: ${Spacing.s};
`;

export const UserQRs: React.FC = () => {
  const userQRs = useAppSelector(QRSliceSelectors.selectAllQRs);

  const { t } = useTranslation();

  const renderQRs = useCallback(
    () =>
      userQRs?.map((qr, i) => (
        <StyledColumn key={qr._id} xs={12} sm={12} md={8} lg={6} xl={4}>
          <QRCode qr={qr} showDeleteButton={true} />
        </StyledColumn>
      )),
    [userQRs],
  );

  if (!userQRs?.length) {
    return (
      <Flex
        alignSelf="center"
        justifyContent="center"
        gap="o"
        height="100%"
        flexDirection="column"
      >
        <Flex
          alignSelf="center"
          justifyContent="center"
          gap="s"
          flexDirection="column"
          width={'80%'}
        >
          <AtiraEmpty
            description={
              <Trans
                i18nKey="qr.no_qrs"
                components={[
                  <PlusIconWrapper>
                    <AtiraIcon icon={faPlus} color="onMain" size="xs" />
                  </PlusIconWrapper>,
                  <br />,
                ]}
              />
            }
          />
          <Text color="fgMuted" align="center">
            {t('qr.noQrs.description')}
            <AtiraIcon icon={faQrcode} />
          </Text>
        </Flex>
      </Flex>
    );
  }

  return <AtiraRow>{renderQRs()}</AtiraRow>;
};
